import { formatCurrency } from "@/common/utils/formatters";
import { EstablishmentProductPriceTableItem } from '../../types/establishmentProducts.type';
import { useState } from "react";
import { useEstablishmentProductPricesHistories } from "../../hooks/useEstablishmentProductPricesHistories";
import { EstablishmentProductPriceHistoriesTransformer } from "../../services/establishmentProductPriceHistoriesTransformer";
import { Mapper } from "@/common/services/mapper";
import TooltipPattern from "@/common/components/TooltipPattern";
import { XIcon } from "lucide-react";
import { LineChart } from "@/common/components/LineChart";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { twMerge } from "tailwind-merge";
import Spinner from "@/common/components/Spinner";


interface PriceValueButtonProps {
  price: EstablishmentProductPriceTableItem;
  clickValueCallback?: Function;
}

const PriceValueButton = ({ price, clickValueCallback }: PriceValueButtonProps) => {
  const [tooltipPriceId, setTooltipPriceId] = useState('');
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (tooltipPriceId !== price.id) {
      setTooltipPriceId(price.id);
      setHoveredElement(e.currentTarget as HTMLElement);
    }
  };

  const handleCloseTooltip = () => {
    setHoveredElement(null);
    setTooltipPriceId('');
  };

  const {
    data: productPriceHistories,
    isPending: isProductPriceHistoriesPending,
  } = useEstablishmentProductPricesHistories({
    id: price.id,
    enabled: tooltipPriceId === price.id,
    mapper: Mapper.mapWithTransformer({ transformer: EstablishmentProductPriceHistoriesTransformer.chartTransformer() }),
  });

  const handleClick = () => {
    if (clickValueCallback) {
      clickValueCallback({
        id: price.id,
        productName: price.product.display_name,
        productId: price.product.id,
        oldPrice: price.value ? formatCurrency(parseFloat(price.value)) : "Não informado",
      });
    }
  };

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleCloseTooltip}>
      <button
        onClick={handleClick}
        title="Editar"
        className={twMerge('bg-neutral-op-8 py-2 px-4 rounded w-full text-left cursor-pointer hover:opacity-75', tooltipPriceId === price.id && 'bg-yellow-op-2')}
        data-testid="edit_product_price_button"
      >
        {price.value ? formatCurrency(parseFloat(price.value)) : "Não informado"}
      </button>

      {tooltipPriceId === price.id && (
        <TooltipPattern target={hoveredElement} expectedWidth={350} expectedHeight={360}>
          <div data-testid="establishment_product_price_histories_tooltip" className="bg-white-200 rounded-lg overflow-hidden border-gray-300 shadow-lg">
            <div className="bg-[#5A5D5F] flex items-center justify-between gap-2 p-6">
              <p className="text-[18px]">
                Histórico de preços
              </p>

              <div className="cursor-pointer" onClick={handleCloseTooltip}>
                <XIcon className="h-6 w-6" color="red" />
              </div>
            </div>

            <div className="bg-[#2c2c2c]">
              {isProductPriceHistoriesPending ? (
                <div className="py-28 px-60">
                  <Spinner className="m-auto" />
                </div>
              ) : (
                <>
                  {productPriceHistories?.data?.length ? (
                    <div className="overflow-y-auto -ms-5 px-6 pt-6 pb-3">
                      <LineChart chartData={productPriceHistories} className="h-[280px]" />
                    </div>
                  ) : (
                    <div className="px-20 py-8">
                      <EmptyDataMessage className="h-min" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </TooltipPattern>
      )}
    </div>
  );
};

export default PriceValueButton;
